import { DocumentNode } from "graphql";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import React, { RefObject } from "react";
import { AppContainerContext, ResponsiveMode } from "@crispico/foundation-react/AppContainerContext";
import { XopsAppContainerContextValue } from "XopsAppContainerContext";
import { Reducers, ReduxReusableComponents, RRCProps, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { AdvancedSelector, AdvancedSelectorItem, AdvancedSelectorRRC } from "@crispico/foundation-react/components/AdvancedSelector/AdvancedSelector";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import _ from "lodash";
import { apolloClient, ENT_READ, EntityDescriptor, Utils } from "@crispico/foundation-react";
import { Button, Icon } from "semantic-ui-react";
import { ModalExt } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import { LOAD_ADDRESSES_FOR_MAP_GO_TO_BUTTON, LOAD_AIRPORTS_FOR_MAP_GO_TO_BUTTON, LOAD_TERRITORIES_FOR_MAP_GO_TO_BUTTON } from "./queries";
import { FindByStringParams } from "@crispico/foundation-react/entity_crud/FindByStringParams";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import { MapContainerLeaflet } from "components/MapContainerLeaflet/MapContainerLeaflet";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { LatLng, LatLngBounds } from "leaflet";
import { MapSettings } from "app";
import { ScriptableContainer } from "@famiprog-foundation/scriptable-ui";

export type MapGoToButtonQueryDetails = {
    query: DocumentNode,
    queryName: string,
    createQueryParams: Function,
    filter?: Filter,
    extraFields?: string[],
}

export type MapGoToButtonOption = {
    label: string,
    queryDetails: MapGoToButtonQueryDetails,
    onSelect?: (entity: any) => void    
}

class MapGoToButtonState extends State {
    isMenuOpened: boolean | [number, number] = false;
    data: { [key: string]: any } = {};
    loadingPerType: { [key: string]: boolean } = {};
    searchValue?: string;
}

class MapGoToButtonReducers<S extends MapGoToButtonState = MapGoToButtonState> extends Reducers<S> {

}

export type MapGoToButtonProps = RRCProps<MapGoToButtonState, MapGoToButtonReducers> & {
    options: { [key: string]: MapGoToButtonOption },
    onSelect?: (type: string, entity: any) => void;
};

const GO_TO_BOTTOM_PADDING = 10;

export function getMapGoToButtonProps(mapContainerRef: RefObject<MapContainerLeaflet>, mapSettings: MapSettings) {
    let mapGoToButtonProps: { [key: string]: any } = {};
    Object.keys(defaultMapGoToButtonProps).forEach(key => {
        if (MapGoToButton.showGoToMapForEntityType(mapSettings, key) && AppMetaTempGlobals.appMetaInstance.hasPermission(Utils.pipeJoin([ENT_READ, key]))) {
            mapGoToButtonProps[key] = _.cloneDeep(defaultMapGoToButtonProps[key]);

            // Here it can be added filters or custom callbacks for entity types
            if (entityDescriptors[key].fields["latitude"] && entityDescriptors[key].fields["longitude"]) {
                mapGoToButtonProps[key].onSelect = (entity: any) => {
                    if (entity.latitude && entity.longitude) {
                        mapContainerRef.current!.getMap().panTo([entity.latitude, entity.longitude]);
                    }
                }
            }
        }
    })

    if (mapGoToButtonProps["Territory"]) {
        mapGoToButtonProps["Territory"].onSelect = async (entity: any) => {
            let northEastLon = -Number.MAX_VALUE;
            let northEastLat = -Number.MAX_VALUE;
            let southWestLon = Number.MAX_VALUE;
            let southWestLat = Number.MAX_VALUE;
            for (let i = 0; i < entity.coordinates.length; i++) {
                if (northEastLon < entity.coordinates[i].a) {
                    northEastLon = entity.coordinates[i].a;
                }
                if (northEastLat < entity.coordinates[i].b) {
                    northEastLat = entity.coordinates[i].b;
                }
                if (southWestLon > entity.coordinates[i].a) {
                    southWestLon = entity.coordinates[i].a;
                }
                if (southWestLat > entity.coordinates[i].b) {
                    southWestLat = entity.coordinates[i].b;
                }
            }
            mapContainerRef.current!.getMap().fitBounds(new LatLngBounds(new LatLng(southWestLat, southWestLon), new LatLng(northEastLat, northEastLon)),
                { padding: [30, 30] });
        }
    }

    return mapGoToButtonProps;
}

export class MapGoToButton extends React.Component<MapGoToButtonProps> {

    static contextType = AppContainerContext;
    context!: XopsAppContainerContextValue;

    advancedSelectorRef = ReduxReusableComponents.createRef<AdvancedSelector>();

    constructor(props: MapGoToButtonProps) {
        super(props);
        this.openMenu = this.openMenu.bind(this);
        this.reloadEntities = this.reloadEntities.bind(this);
        this.onSelectEntity = this.onSelectEntity.bind(this);
    }

    static showGoToMapForEntityType(mapSettings: MapSettings, value: string): boolean {
        return Utils.isNullOrEmpty(mapSettings.goToButtonEntityTypes) ? true : mapSettings.goToButtonEntityTypes!.split(",").map(item => item.trim()).includes(value);
    }

    private initLoadingPerType(inProgress: boolean) {
        let loadingPerType: { [key: string]: boolean } = {};
        Object.keys(this.props.options).forEach((key) => { loadingPerType[key] = inProgress; });

        return loadingPerType;
    }

    protected getEmptyData() {
        let data: { [key: string]: any } = {};
        Object.keys(this.props.options).forEach((key) => {
            data[key] = [];
        });

        return data;
    }

   async componentDidMount() {
        await this.props.r.setInReduxState({ loadingPerType: this.initLoadingPerType(true), data: this.getEmptyData() });
        this.componentDidUpdateInternal();
    }

    componentDidUpdate(prevProps: Readonly<MapGoToButtonProps>) {
        this.componentDidUpdateInternal(prevProps);
    }

    async componentDidUpdateInternal(prevProps?: Readonly<MapGoToButtonProps>) {
        if (!prevProps || prevProps.s.loadingPerType !== this.props.s.loadingPerType) {
            let data: { [key: string]: any } = {};
            let loadingPerType = _.cloneDeep(this.props.s.loadingPerType);

            Object.keys(loadingPerType).forEach((key) => {
                data[key] = this.props.s.data[key];
            });

            for (let key of Object.keys(this.props.options)) {
                if (loadingPerType[key] === true) {
                    let result = await this.performQuery(entityDescriptors[key], this.props.s.searchValue, this.props.options[key].queryDetails.filter);
                    data[key] = result;
                    loadingPerType[key] = false;

                    this.props.r.setInReduxState({ loadingPerType, data });
                    return;
                }
            }
            this.advancedSelectorRef.current?.checkItemsNumberAndClick();
        }
    }

    protected async performQuery(entityDescriptor: EntityDescriptor, searchQuery?: string, filter?: Filter) {
        let result = (await apolloClient.query({
            query: this.props.options[entityDescriptor.name].queryDetails.query,
            variables: this.props.options[entityDescriptor.name].queryDetails.createQueryParams(filter, searchQuery),
            context: { showSpinner: false }
        })).data[this.props.options[entityDescriptor.name].queryDetails.queryName];

        return result;
    }

    protected async openMenu() {
        const rect = document.getElementById("goToButton")!.getBoundingClientRect();
        this.props.r.setInReduxState({ isMenuOpened: [rect.left, rect.bottom + GO_TO_BOTTOM_PADDING] });
    }

    protected onSelectEntity(item: AdvancedSelectorItem) {
        if (this.props.options[item.data["type"]]) {
            this.props.options[item.data["type"]].onSelect!(item.data["entity"]);
        }
        this.props.onSelect?.call(null, item.data.type, item.data.entity.id);

        this.props.r.setInReduxState({ isMenuOpened: false, searchValue: undefined });
    }

    protected getSpecialAdvancedSelectorItem(id: string, text: string) {
        return {
            id,
            level: 2,
            text,
            color: "grey"
        } as AdvancedSelectorItem;
    }

    private getAdvancedSelectorData() {
        let data: AdvancedSelectorItem[] = [];
        Object.keys(this.props.options).forEach((key) => {
            data.push({
                id: key,
                level: 1,
                text: this.props.options[key].label,
                color: undefined,
                icon: entityDescriptors[key].icon,
                className: "MapGoToButton_header"
            });
            if (this.props.s.loadingPerType[key]) {
                data.push(this.getSpecialAdvancedSelectorItem(key + "_loadingDataLabel", _msg("MapGoToButton.loadingData.label")));
            } else if (this.props.s.data[key].length === 0) {
                data.push(this.getSpecialAdvancedSelectorItem(key + "_noData", _msg("MapGoToButton.noOptions.label")));
            }
            else {
                if (this.props.s.data[key].length === 10) {
                    data.push(this.getSpecialAdvancedSelectorItem(key + "_moreDataLabel", _msg("MapGoToButton.moreOptions.label")));
                }
                this.props.s.data[key].forEach((option: any) => {
                    let extraFieldsValues = "";
                    if (this.props.options[key].queryDetails.extraFields) {
                        this.props.options[key].queryDetails.extraFields?.forEach((field: any) => {
                            extraFieldsValues += " " + Utils.navigate(option, field, true, ".");
                        })
                    }
                    data.push({
                        id: key + "_" + option.id,
                        level: 3,
                        text: entityDescriptors[key].toMiniString(option) + extraFieldsValues,
                        color: undefined,
                        data: {
                            type: key,
                            entity: option
                        },
                        disabled: (option["latitude"] === null) && (option["longitude"] === null),
                        tooltip: _msg("MapGoToButton.noCoordinates.label", key)
                    })
                });
            }
        });
        return data;
    }

    private reloadEntities(searchValue: string) {
        this.props.r.setInReduxState({ loadingPerType: this.initLoadingPerType(true), searchValue });
    }

    render() {
        const phoneMode: boolean = this.context.responsiveState.mode !== ResponsiveMode.DESKTOP;
        if (Object.keys(this.props.s.data).length === 0) {
            return <></>;
        }
        return <ScriptableContainer id="MapGoToButton">
            <Button color='olive' icon={phoneMode} id="goToButton" onClick={this.openMenu}>
                <Icon name="paper plane outline" />
                {!phoneMode && _msg("MapRealTime.goTo")}
            </Button>
            <ModalExt
                onClose={() =>this.props.r.setInReduxState({ isMenuOpened: false, searchValue: undefined })}
                open={this.props.s.isMenuOpened}
                size='mini'
                className="MapGoToButton_Modal"
            >
                <AdvancedSelectorRRC ref={this.advancedSelectorRef} data={this.getAdvancedSelectorData()} id={"GoToSelectorRRC"}
                    onItemClick={this.onSelectEntity} searchActionProps={{ icon: "refresh" }} onSearchedValueChanged={(searchedValue) => {
                        this.reloadEntities(searchedValue);
                    }} />
            </ModalExt>
        </ScriptableContainer>
    }
}

export const MapGoToButtonRRC = ReduxReusableComponents.connectRRC(MapGoToButtonState, MapGoToButtonReducers, MapGoToButton);

export const defaultMapGoToButtonProps: { [key: string]: any } = {
    "Address": {
        label: _msg("Address.label.plural"),
        queryDetails: {
            query: LOAD_ADDRESSES_FOR_MAP_GO_TO_BUTTON,
            queryName: "addressService_findByString",
            createQueryParams: (filter: Filter, searchQuery: string) => FindByStringParams.create().filter(filter).string(searchQuery ? searchQuery : "")
        }
    },
    "Airport": {
        label: _msg("Airport.label.plural"),
        queryDetails: {
            query: LOAD_AIRPORTS_FOR_MAP_GO_TO_BUTTON,
            queryName: "airportService_findByString",
            createQueryParams: (filter: Filter, searchQuery: string) => FindByStringParams.create().filter(filter).string(searchQuery ? searchQuery : ""),
            extraFields: ["name"],
            filter: Filter.createComposed(FilterOperators.forComposedFilter.and, [
                Filter.create("latitude", FilterOperators.forNumber.isNotEmpty),
                Filter.create("longitude", FilterOperators.forNumber.isNotEmpty)
            ])
        }
    },
    "Territory": {
        label: _msg("Territory.label.plural"),
        queryDetails: {
            query: LOAD_TERRITORIES_FOR_MAP_GO_TO_BUTTON,
            queryName: "territoryService_findByString",
            createQueryParams: (filter: Filter, searchQuery: string) => FindByStringParams.create().filter(filter).string(searchQuery ? searchQuery : "")
        }
    },
}